/**
 * logUtils.js — shaping helpers for the Ocean API Logs screen.
 *
 * The heavy lifting already happened server-side: oceanQueryLog.service's
 * `toLogRow` derives route / carriers / containers / status from the two
 * verbatim Mixed payloads, so everything here is presentation only — reading
 * the derived row into camelCase-stable locals, and formatting.
 */
import moment from "moment-timezone";

export const IST = "Asia/Kolkata";

/**
 * Visual identity per `type` enum value (models/oceanQueryLog.model.js). The
 * labels mirror the backend's TYPE_LABELS, but a row's own `typeLabel` always
 * wins — the backend is the source of truth for wording.
 */
export const LOG_TYPE_META = {
  rate_search: {
    label: "Freightify Rate Search",
    dot: "#3B82F6",
    chip: "bg-blue-50 text-blue-700",
  },
  // Two separate steps, two separate tags. `booking` is the create-booking call
  // (/create-ocean-booking, /kam/ocean-booking) — it only ever leaves the query
  // in "Confirmation Awaited", so amber, not green. `booking_confirmed` is
  // /api/kam/ocean-booking/confirm, where the KAM supplies the MBL number and
  // the shipment is actually booked — that one keeps the green.
  booking: {
    label: "Confirmation Awaited",
    dot: "#F59E0B",
    chip: "bg-amber-50 text-amber-700",
  },
  booking_confirmed: {
    label: "Ocean Booking",
    dot: "#22C55E",
    chip: "bg-green-50 text-green-700",
  },
  booking_update: {
    label: "Booking Update",
    dot: "#0EA5E9",
    chip: "bg-sky-50 text-sky-700",
  },
  booking_cancel: {
    label: "Booking Cancel",
    dot: "#EF4444",
    chip: "bg-red-50 text-red-700",
  },
};

const FALLBACK_TYPE_META = { label: "API Call", dot: "#94A3B8", chip: "bg-slate-100 text-slate-600" };

export const getLogTypeMeta = (type) => LOG_TYPE_META[type] || FALLBACK_TYPE_META;

/**
 * Two facets the table shows as their own tag, neither of which is a stored
 * `type`: a call that FAILED, and a rate search that re-quotes a query already
 * searched before. Both are written as a suffix on the type — `rate_search`,
 * `rate_search~update`, `rate_search:error` — so one string identifies the tag
 * in a row, in a group's tag list, and in the type filter.
 */
export const ERROR_SUFFIX = ":error";
export const UPDATE_SUFFIX = "~update";

/** Failure wins the colour: a failed booking reads as an error, not a booking. */
const ERROR_META = { dot: "#EF4444", chip: "bg-red-50 text-red-700" };
const UPDATE_META = { dot: "#8B5CF6", chip: "bg-violet-50 text-violet-700" };

/** `rate_search:error` → `rate_search`; anything already bare is unchanged. */
export const baseTypeOf = (variant) => String(variant || "").split(ERROR_SUFFIX)[0].split(UPDATE_SUFFIX)[0];
export const isErrorVariant = (variant) => String(variant || "").endsWith(ERROR_SUFFIX);
const isUpdateVariant = (variant) => String(variant || "").includes(UPDATE_SUFFIX);

/** Chip identity + label for a variant, derived from its base type's meta. */
export const getVariantMeta = (variant) => {
  const base = getLogTypeMeta(baseTypeOf(variant));
  const update = isUpdateVariant(variant);
  const error = isErrorVariant(variant);

  return {
    dot: error ? ERROR_META.dot : update ? UPDATE_META.dot : base.dot,
    chip: error ? ERROR_META.chip : update ? UPDATE_META.chip : base.chip,
    label: `${base.label}${update ? " Update" : ""}${error ? " Error" : ""}`,
  };
};

/**
 * Type dropdown options: each stored type followed by its error variant, so
 * "just the failures" of one kind is one selection. The re-search variant is
 * NOT offered — filtering "Freightify Rate Search" is meant to include the
 * re-quotes of a query, which is what the backend's `type` param does.
 */
export const TYPE_FILTER_OPTIONS = Object.entries(LOG_TYPE_META).flatMap(([value, meta]) => [
  { value, label: meta.label },
  { value: `${value}${ERROR_SUFFIX}`, label: `${meta.label} Error` },
]);

const asId = (value) => {
  if (!value) return null;
  // A populated ref arrives as an object; an unpopulated one as a bare id.
  if (typeof value === "object") return value._id ? String(value._id) : null;
  return String(value);
};

/**
 * A serial as the table prints it. `sno` / `querySno` are assigned by the
 * backend (per log, and per query), so a row written before they existed has
 * none — hence the dash rather than a made-up 0.
 */
export const formatSno = (value) =>
  value === null || value === undefined || value === "" ? "—" : String(value);

/** First serial-shaped value among the candidates; null when none carries one. */
const pickSno = (...values) => {
  const found = values.find((value) => value !== null && value !== undefined && value !== "");
  return found === undefined ? null : found;
};

/**
 * The query's serial off a log row or a group.
 *
 * `querySno` is flattened next to the ids on every log endpoint, but the query
 * ref is also populated with `sno` selected — so a shape that carries the
 * query as an object (`?raw=true`, or a populated `queryId`) is read there
 * rather than reported as missing.
 */
const readQuerySno = (source = {}) =>
  pickSno(
    source.querySno,
    typeof source.queryId === "object" ? source.queryId?.sno : undefined,
    source.query?.sno,
  );

/** ObjectIds are too long for a table cell — show the tail, keep the whole one in `title`. */
export const shortId = (id) => {
  const text = asId(id);
  if (!text) return "—";
  return text.length > 10 ? `…${text.slice(-8)}` : text;
};

/**
 * Ports are stored as `"Nhava Sheva Sea - (INNSA1)"`; the Route column only has
 * room for the UN/LOCODE, so pull it out of the trailing parens. Anything not in
 * that shape (a bare code, a plain city name) is returned as-is.
 */
export const portCode = (port) => {
  const text = typeof port === "string" ? port.trim() : "";
  if (!text) return "";
  const match = text.match(/\(([^()]+)\)\s*$/);
  return (match ? match[1] : text).trim();
};

/**
 * The unique shipping lines on a row/group as `{ name, code, logo }`.
 *
 * The backend derives these from each offer's `meta.serviceProvider`, which is
 * where the logo lives. Rows read through an older build only carried the
 * `carriers` name array, so those degrade to name-only entries (no logo) —
 * hence the fallback rather than an empty list.
 */
const normalizeShippingLines = (source = {}) => {
  if (Array.isArray(source.shippingLines) && source.shippingLines.length) {
    return source.shippingLines
      .map((line) => ({
        name: line?.name || line?.code || "",
        code: line?.code || null,
        logo: line?.logo || null,
      }))
      .filter((line) => line.name);
  }

  return (Array.isArray(source.carriers) ? source.carriers : [])
    .filter((name) => typeof name === "string" && name)
    .map((name) => ({ name, code: null, logo: null }));
};

/** Keys a booked line's name / code can sit under, folded to letters only. */
const LINE_NAME_KEYS = new Set(["carriername", "shippinglinename", "linename", "vendorname"]);
const LINE_CODE_KEYS = new Set(["carrierscac", "scac", "carriercode", "shippinglinecode", "scheduleprovidercode"]);
/** Objects that describe a line as `{ name, code, logo }` rather than flat keys. */
const LINE_OBJECT_KEYS = new Set(["serviceprovider", "carrier", "shippingline", "vendor"]);

const fold = (key) => String(key).toLowerCase().replace(/[^a-z]/g, "");
const asText = (value) => (typeof value === "string" && value.trim() ? value.trim() : "");

/**
 * Depth- and node-bounded search for a line inside a logged payload.
 *
 * Bounded on purpose: a booking payload embeds the whole offer (charges,
 * schedules, legs), and this runs for every row of a 2000-log window — so it
 * stops at the first name it finds and gives up after a fixed node budget
 * rather than walking the entire tree.
 */
const scanForLine = (root) => {
  const found = { name: "", code: "", logo: null };
  let budget = 600;

  const readLineObject = (value) => {
    if (!found.name) found.name = asText(value.name);
    if (!found.code) found.code = asText(value.code) || asText(value.scac) || asText(value.scheduleProviderCode);
    if (!found.logo) found.logo = asText(value.logo) || null;
  };

  const walk = (node, depth) => {
    if (!node || typeof node !== "object" || depth > 6 || budget <= 0) return;
    budget -= 1;

    if (Array.isArray(node)) {
      node.forEach((item) => walk(item, depth + 1));
      return;
    }

    Object.entries(node).forEach(([rawKey, value]) => {
      if (budget <= 0 || (found.name && found.code)) return;
      const key = fold(rawKey);

      if (typeof value === "string") {
        if (!found.name && LINE_NAME_KEYS.has(key)) found.name = asText(value);
        else if (!found.code && LINE_CODE_KEYS.has(key)) found.code = asText(value);
        return;
      }

      if (value && typeof value === "object") {
        if (!Array.isArray(value) && LINE_OBJECT_KEYS.has(key)) readLineObject(value);
        walk(value, depth + 1);
      }
    });
  };

  walk(root, 0);
  return found;
};

/**
 * The line a booking / booking-update / booking-cancel was made with.
 *
 * The backend only derives `shippingLines` from rate-search offers, so these
 * rows arrive with none even though the payload names the line: our own
 * booking calls carry it at `ocean_offer.productOffer.carrierName`, which is
 * why that path is read first. Endpoint payloads differ (and change), so an
 * unrecognised shape falls back to the bounded scan above — request first, then
 * the response, which is where a cancel/confirm echo usually names the line.
 * Returns [] when nothing names it, never a placeholder.
 */
const deriveBookingLines = (row = {}) => {
  const body = row.request?.body ?? row.request ?? null;
  const offer = body?.ocean_offer?.productOffer;

  const direct = { name: asText(offer?.carrierName), code: asText(offer?.carrierScac), logo: null };
  const found = direct.name || direct.code ? direct : scanForLine(body);
  const line = found.name || found.code ? found : scanForLine(row.response?.body ?? row.response ?? null);

  if (!line.name && !line.code) return [];
  return [{ name: line.name || line.code, code: line.code || null, logo: line.logo || null }];
};

/**
 * A line's identity for the filter: its folded name.
 *
 * Not the code — `shippingLines[].code` only exists on rows written once the
 * backend started deriving lines from `meta.serviceProvider`; older rows carry
 * name-only entries (see normalizeShippingLines). The name is the one field
 * always present, so folding on it is what keeps "CMA CGM" logged both ways
 * from showing up as two separate options.
 */
export const shippingLineValue = (line) =>
  typeof line?.name === "string" ? line.name.trim().toLowerCase() : "";

/**
 * Every unique line across the given groups' attempts, as Select options
 * sorted by name — the dropdown is built from the data actually loaded, so it
 * never offers a line that can't match anything in the window.
 *
 * A line whose logo was only logged on some attempts keeps the logo it has,
 * and blank/absent names are skipped rather than becoming an empty option.
 */
export const collectShippingLines = (groups = []) => {
  const byValue = new Map();

  groups.forEach((group) => {
    (group?.logs || []).forEach((log) => {
      (log?.shipping_lines || []).forEach((line) => {
        const value = shippingLineValue(line);
        if (!value) return;
        const existing = byValue.get(value);
        if (!existing) {
          byValue.set(value, { value, label: line.name.trim(), logo: line.logo || null });
        } else if (!existing.logo && line.logo) {
          existing.logo = line.logo;
        }
      });
    });
  });

  return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * A derived row from GET /api/ocean-query-log. Field names are taken straight
 * from `toLogRow`; the defaults only cover a row written before a field
 * existed, so nothing here invents data.
 */
export const normalizeLog = (row = {}) => {
  const type = row.type || "rate_search";
  // A rate search's lines come off its offers; a booking/update/cancel has to
  // be read out of the payload (and only then — no point scanning a search).
  const logged = normalizeShippingLines(row);
  const lines = logged.length || type === "rate_search" ? logged : deriveBookingLines(row);
  const failed = Boolean(row.failed);
  // Derived from the lines whenever the backend logged none of its own — which
  // is every booking/cancel row. The count has to be read off the SAME list, or
  // a group with no rate search in it collapses to "0 lines" while its attempt
  // rows plainly show a chip.
  const carriers = Array.isArray(row.carriers) && row.carriers.length ? row.carriers : lines.map((line) => line.name);

  return {
    id: asId(row._id) || asId(row.id),
    // Serials ride alongside the ids on every log endpoint: `sno` counts the
    // log, `querySno` the query it belongs to.
    sno: pickSno(row.sno),
    query_id: asId(row.queryId),
    query_sno: readQuerySno(row),
    user_id: asId(row.userId),
    type,
    type_label: row.typeLabel || getLogTypeMeta(row.type).label,
    // The tag the table shows. `is_update` can only be settled once the other
    // attempts on the query are known, so normalizeGroup revises both.
    is_update: false,
    variant: failed ? `${type}${ERROR_SUFFIX}` : type,
    origin: row.route?.origin || null,
    destination: row.route?.destination || null,
    shipping_lines: lines,
    carriers,
    carriers_count: row.carriersCount ?? carriers.length,
    offers_count: row.offersCount ?? null,
    container_load: row.containerLoad || null,
    total_containers: row.totalContainers ?? null,
    total_weight: row.totalWeight ?? null,
    // The account the row belongs to — the query's owner, per toLogRow.
    requested_by: row.requestedBy || row.requestedByEmail || "—",
    requested_by_email: row.requestedByEmail || null,
    // Who made the call. Differs from the above when a KAM acts for a customer.
    acted_by: row.actedBy || row.actedByEmail || null,
    acted_by_email: row.actedByEmail || null,
    method: row.method || null,
    url: row.url || null,
    // Freightify's rate call carries its query string separately (it's logged
    // as `params.toString()`), so the modal's URL has to re-join them.
    params: row.request?.params || null,
    status_code: row.statusCode ?? null,
    failed,
    success: row.success !== undefined ? Boolean(row.success) : !failed,
    created_at: row.createdAt || null,
    request: row.request ?? null,
    response: row.response ?? null,
    // Listing rows arrive without their full payloads; the detail modal reads
    // them from GET /:id when this is set.
    payloads_omitted: Boolean(row.payloadsOmitted),
    // The response's `error` is what makes a row failed when there's no status.
    error: row.response?.error ?? null,
  };
};

/**
 * Marks every rate search after a query's FIRST one as an update, in place.
 *
 * Logs arrive newest-first, so the last rate search in the array is the one
 * that opened the query; searching the same query again (a re-quote) is what
 * the table tags "Freightify Rate Search Update". A group with a single search
 * is left alone — nothing was re-quoted.
 */
const markResearchUpdates = (logs) => {
  const searchIndexes = logs.reduce((acc, log, index) => (log.type === "rate_search" ? [...acc, index] : acc), []);
  if (searchIndexes.length < 2) return logs;

  // Every search but the oldest (highest index, since newest is first).
  searchIndexes.slice(0, -1).forEach((index) => {
    const log = logs[index];
    log.is_update = true;
    log.variant = `${log.type}${UPDATE_SUFFIX}${log.failed ? ERROR_SUFFIX : ""}`;
  });

  return logs;
};

/**
 * Everything a collapsed row says about its attempts. A row describes the
 * attempts it actually holds — and since every filter is applied server-side,
 * those are exactly the ones that matched.
 */
const deriveGroupFacets = (logs) => {
  const [latest] = logs;

  // Distinct tags in the group, newest first — usually just rate_search, but a
  // query that was searched, re-searched and then booked carries several.
  const types = [];
  const seen = new Set();
  logs.forEach((log) => {
    if (seen.has(log.variant)) return;
    seen.add(log.variant);
    types.push({ key: log.variant, label: getVariantMeta(log.variant).label });
  });

  // Containers/weight describe the search itself, so the latest attempt is the
  // honest value for the collapsed row. Booking/cancel attempts only resolve a
  // count while their saved query is still around, so an attempt that reports
  // none defers to the newest one that does — better than collapsing a group
  // that plainly has containers down to 0.
  const sized = logs.find((log) => log.total_containers) || latest;

  // Lines are reported from the NEWEST rate search alone, not the union across
  // attempts: a re-search re-quotes the same query, so the union double-counts
  // lines that have since dropped out, and booking/cancel attempts quote none.
  // A newest search that came back empty (or failed) defers to the last one
  // that actually quoted, same as `sized` above.
  const searches = logs.filter((log) => log.type === "rate_search");
  const quoted = searches.find((log) => log.carriers_count) || searches[0] || latest;

  return {
    logs_count: logs.length,
    failed_count: logs.filter((log) => log.failed).length,
    shipping_lines: quoted?.shipping_lines ?? [],
    carriers: quoted?.carriers ?? [],
    carriers_count: quoted?.carriers_count ?? 0,
    total_containers: sized?.total_containers ?? null,
    total_weight: sized?.total_weight ?? null,
    container_load: sized?.container_load || latest?.container_load || null,
    types,
  };
};

/** A group from GET /api/ocean-query-log?groupBy=query. */
export const normalizeGroup = (group = {}) => {
  const logs = markResearchUpdates(Array.isArray(group.logs) ? group.logs.map(normalizeLog) : []);
  const [latest] = logs;
  const facets = deriveGroupFacets(logs);

  return {
    key: asId(group.queryId) || `orphan-${latest?.id || Math.random().toString(36).slice(2)}`,
    query_id: asId(group.queryId),
    query_sno: pickSno(readQuerySno(group), latest?.query_sno),
    origin: group.route?.origin || latest?.origin || null,
    destination: group.route?.destination || latest?.destination || null,
    requested_by: group.requestedBy || latest?.requested_by || "—",
    user_id: asId(group.userId),
    created_at: group.createdAt || latest?.created_at || null,
    ...facets,
    // The aggregation's own counts win for the untouched group — they're
    // authoritative even if a log were ever omitted from the embedded array.
    logs_count: group.logsCount ?? facets.logs_count,
    failed_count: group.failedCount ?? facets.failed_count,
    logs,
  };
};

/**
 * GET /api/ocean-query-log/stats → { total, failed, byType, typeLabels }.
 *
 * Booking updates stay folded into BOOKINGS (with the split kept alongside for
 * the tooltip) so that rate search + confirmation awaited + bookings +
 * cancelled still reconciles with the total — an update is an amendment to a
 * booking, not a step of its own.
 */
export const normalizeStats = (stats = {}) => {
  const byType = stats.byType || {};
  // `booking` is the create call, which only reaches "Confirmation Awaited";
  // `booking_confirmed` is /kam/ocean-booking/confirm.
  const awaited = byType.booking || 0;
  const confirmed = byType.booking_confirmed || 0;
  const updates = byType.booking_update || 0;

  return {
    total: stats.total || 0,
    failed: stats.failed || 0,
    rate_search: byType.rate_search || 0,
    confirmation_awaited: awaited,
    bookings: confirmed + updates,
    new_bookings: confirmed,
    booking_updates: updates,
    booking_cancel: byType.booking_cancel || 0,
  };
};

/**
 * GET /api/ocean-query-log/user-counts → [{ userId, requestedBy, email, total, byType }].
 *
 * Meant to be used as `rows.map(normalizeUserCount)`, so `index` arrives for
 * free. A userId is unique per aggregated row and makes the best React key;
 * without one the index is appended so id-less rows (shared email, or the
 * "unknown" bucket) can't collide on the same key.
 */
export const normalizeUserCount = (row = {}, index = 0) => {
  const byType = row.byType || {};
  return {
    key: asId(row.userId) || `${row.email || "unknown"}-${index}`,
    user_id: asId(row.userId),
    requested_by: row.requestedBy || row.email || "Unknown user",
    email: row.email || null,
    total: row.total || 0,
    rate_search: byType.rate_search || 0,
    // Same split as normalizeStats: the create call vs. the KAM's confirm.
    confirmation_awaited: byType.booking || 0,
    bookings: byType.booking_confirmed || 0,
    booking_updates: byType.booking_update || 0,
    booking_cancel: byType.booking_cancel || 0,
  };
};

/**
 * The "User-wise Count" tab's own sort. It has no server param — the
 * /user-counts aggregation returns every matching user in one response, so the
 * ordering (and the search below) is applied to those rows.
 */
export const USER_SORT_OPTIONS = [
  { value: "total", label: "Total activity" },
  { value: "rate_search", label: "Rate search" },
  { value: "confirmation_awaited", label: "Confirmation awaited" },
  { value: "bookings", label: "Booking" },
  { value: "booking_cancel", label: "Cancellations" },
  { value: "name", label: "Name (A-Z)" },
];

/** Bookings and booking updates share one column, so they sort as one number. */
const bookingsOf = (row) => row.bookings + row.booking_updates;

/** Rows ordered by the chosen sort — counts descending, name ascending. */
export const sortUserRows = (rows = [], sortBy = "total") => {
  const sorted = [...rows];
  if (sortBy === "name") {
    return sorted.sort((a, b) => a.requested_by.localeCompare(b.requested_by));
  }
  const valueOf = (row) => (sortBy === "bookings" ? bookingsOf(row) : row[sortBy] ?? 0);
  // Ties fall back to the total, so a shared 0 on the sorted column still
  // lists the busier user first.
  return sorted.sort((a, b) => valueOf(b) - valueOf(a) || b.total - a.total);
};

/** Substring match on the display name or the email. Blank matches everything. */
export const matchesUserSearch = (row, search) => {
  const text = String(search || "").trim().toLowerCase();
  if (!text) return true;
  return `${row.requested_by || ""} ${row.email || ""}`.toLowerCase().includes(text);
};

/** Up to two initials for the avatar; "?" when the name is unusable. */
export const initialsOf = (name) => {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join("");
};

export const formatCreatedAt = (value) => {
  if (!value) return "—";
  const parsed = moment.tz(value, IST);
  return parsed.isValid() ? `${parsed.format("D MMM YYYY, hh:mm:ss a")} IST` : String(value);
};

export const formatNumber = (value) =>
  value === null || value === undefined ? "—" : Number(value).toLocaleString("en-IN");

/** The URL actually called — Freightify's params are logged apart from the URL. */
export const fullRequestUrl = (log) => {
  if (!log?.url) return "";
  return log.params ? `${log.url}?${log.params}` : log.url;
};

/**
 * Body payloads are stored verbatim, so they can be an object, a JSON string,
 * or (for a gateway error page) a non-JSON string. `pretty` re-indents what
 * parses and leaves the rest alone.
 */
export const toJsonText = (value, pretty = true) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") {
    if (!pretty) return value;
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch (err) {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, pretty ? 2 : 0);
  } catch (err) {
    return String(value);
  }
};

const escapeHtml = (text) => String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const JSON_TOKEN =
  /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

/**
 * Minimal JSON colouriser for the dark payload panes. Escaping runs first, so
 * the result is safe for dangerouslySetInnerHTML.
 */
export const highlightJson = (text) =>
  escapeHtml(text).replace(JSON_TOKEN, (match) => {
    let color = "#34D399"; // numbers
    if (/^"/.test(match)) {
      color = /:$/.test(match) ? "#E2E8F0" : "#FBBF24"; // key : value
    } else if (/true|false|null/.test(match)) {
      color = "#C084FC";
    }
    return `<span style="color:${color}">${match}</span>`;
  });

export const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // Fall through — the clipboard API needs a secure context, which staging
    // over plain http isn't.
  }

  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch (err) {
    return false;
  }
};

export const downloadTextFile = (text, filename) => {
  const blob = new Blob([text ?? ""], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const safeDecode = (text) => {
  try {
    return decodeURIComponent(String(text).replace(/\+/g, " "));
  } catch (err) {
    // A malformed %-escape in a logged URL must not take the modal down.
    return String(text);
  }
};

/**
 * Splits a logged query string into ordered key/value pairs.
 * @param {String} params - Query string, with or without a leading "?".
 * @returns {{key: String, value: String}[]}
 */
export const parseQueryParams = (params) => {
  if (!params) return [];
  const text = String(params).replace(/^\?/, "");
  if (!text) return [];

  return text
    .split("&")
    .filter(Boolean)
    .map((pair) => {
      const split = pair.indexOf("=");
      return split === -1
        ? { key: safeDecode(pair), value: "" }
        : { key: safeDecode(pair.slice(0, split)), value: safeDecode(pair.slice(split + 1)) };
    });
};

/**
 * The ocean rate search is a GET whose entire payload IS its query string
 * (~28 params: mode, pricingViews, the origin/destination service-mode flags,
 * containers, cargoReadyDateFrom, ...), logged apart from the URL as
 * `params.toString()`. This folds it into ONE flat object so the modal can
 * render the request as JSON like any other payload.
 *
 * Blank params are kept as "" rather than dropped - they were sent, and a
 * blank haulageMode is meaningful. A repeated key collects into an array, so
 * nothing is silently lost to a last-one-wins overwrite.
 * @param {String} params - Query string, with or without a leading "?".
 * @returns {Object} e.g. { mode: "FCL", searchCurrency: "USD", ... }
 */
export const queryParamsToObject = (params) =>
  parseQueryParams(params).reduce((acc, { key, value }) => {
    if (Object.prototype.hasOwnProperty.call(acc, key)) {
      acc[key] = Array.isArray(acc[key]) ? [...acc[key], value] : [acc[key], value];
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
